import {useEffect, useMemo, useRef, useState} from "react";
import {LocalUpload, Upload} from "@/Pages/Dashboard/Upload/UploadModal";
import {cancelGpuCapacity, reserveGpuCapacity} from "@/services/extractionJobManager";
import {getMediaDuration} from "@/utils/fileUtils";

type warmupProps = {
    uploads: Upload[],
    totalUploadEta: number,
    uploadDurations: Record<string, number>,
    enabled: boolean,
    userCredits: number
}

const maxUploadEta = 90;
const minUploadEta = 15;
const maxWarmupsPerBatch = 3;
const maxWarmupsPerSession = 2;

type ReturnType = {
    reservedInstances: Record<string, Date>,
    isReservationActive: boolean,
    cancelReservation: (uploadIds: string[] | null, reason?: string) => void,
    commitReservations: () => void,
    getReservedInstance: (uploadId: string) => Date | undefined
}

export default function useComputeReservation({
    uploads,
    uploadDurations,
    totalUploadEta,
    enabled,
    userCredits
}: warmupProps
): ReturnType {
    const [reservedUploadIds, setReservedUploadIds] = useState<Record<string, Date>>({});
    const reservedUploadIdsRef = useRef<Record<string, Date>>({});
    const timeoutsRef = useRef<number[]>([]);
    const [failedReservations, setFailedReservations] = useState<string[]>([]);
    const [fileDurationCache, setFileDurationCache] = useState<Record<string, number>>({});
    const [isRunning, setIsRunning] = useState(false);
    const isRunningRef = useRef(false);
    const processingDurations = useRef<Set<string>>(new Set());
    const minUploadFileDuration = useMemo(() => laravelConfig<number>('aws.job_audio_cpu_threshold'), []);
    const creditsConsumed = useMemo(() =>
        Object.keys(reservedUploadIds).reduce(
            (total, id) => total + Math.ceil((uploadDurations[id] || fileDurationCache[id]) / 60),
            0
        ),
        [reservedUploadIds]
    )

    const cancelReservation = (uploadIds: string[] | null, reason: string = '') => {
        uploadIds ??= Object.keys(reservedUploadIdsRef.current);
        if (!uploadIds.length) {
            return;
        }
        uploadIds.forEach(id => delete reservedUploadIdsRef.current[id]);
        setReservedUploadIds(prev => {
            const newReserved = {...prev};
            uploadIds.forEach(id => delete newReserved[id]);
            return newReserved;
        });
        cancelGpuCapacity(uploadIds, reason).catch(() => {});
    }

    useEffect(() => {
        Object.keys(reservedUploadIdsRef.current)
            .filter(id => !uploads.some(upload => upload.id === id))
            .forEach(id => delete reservedUploadIdsRef.current[id]);

        setReservedUploadIds(prev => {
            const filtered = Object.fromEntries(
                Object.entries(prev).filter(([id]) =>
                    uploads.some(upload => upload.id === id)
                )
            )

            if (Object.keys(filtered).length !== Object.keys(prev).length) {
                return filtered
            }

            return prev
        })
    }, [uploads.length]);

    useEffect(() => {
        if(!uploads.length && timeoutsRef.current.length) {
            timeoutsRef.current.forEach(t => clearTimeout(t))
            timeoutsRef.current = [];
        }
    }, [uploads.length]);

    useEffect(() => {
        if(!enabled) {
            return;
        }

        uploads.filter(upload => 'file' in upload)
            .forEach(async upload => {
                if (
                    !uploadDurations[upload.id] &&
                    !fileDurationCache[upload.id] &&
                    !processingDurations.current.has(upload.id)
                ) {
                    try {
                        processingDurations.current.add(upload.id);
                        const file = (upload as LocalUpload).file;
                        const duration = await getMediaDuration(file);
                        if (duration) {
                            setFileDurationCache(prev => ({...prev, [upload.id]: duration}));
                        }
                    } finally {
                        processingDurations.current.delete(upload.id);
                    }
                }
            })
    }, [uploads, uploadDurations, enabled]);

    useEffect(() => {
        if (
            !enabled ||
            isRunningRef.current ||
            totalUploadEta < minUploadEta ||
            totalUploadEta > maxUploadEta
        ) {
            return;
        }

        const filteredUploads = uploads.filter(upload =>
            (uploadDurations[upload.id] || fileDurationCache[upload.id] || 0) >= minUploadFileDuration &&
            !reservedUploadIds[upload.id] &&
            !failedReservations.includes(upload.id)
        )

        const maxWarmups = Math.min(maxWarmupsPerBatch, maxWarmupsPerSession - Object.keys(reservedUploadIds).length);
        const uploadsToWarmup = filteredUploads.slice(0, maxWarmups).map(up => up.id);
        const creditsNeeded = uploadsToWarmup.reduce(
            (sum, id) => sum + Math.ceil((uploadDurations[id] || fileDurationCache[id]) / 60),
            0
        );

        if(!uploadsToWarmup.length || (userCredits - creditsConsumed) < creditsNeeded) {
            return;
        }

        isRunningRef.current = true;
        setIsRunning(true);

        reserveGpuCapacity(uploadsToWarmup).then(({ success, expires_seconds }) => {
            if(success) {
                const reserved = Object.fromEntries(uploadsToWarmup.map(id => [id, new Date()]));
                Object.assign(reservedUploadIdsRef.current, reserved);
                setReservedUploadIds(prev => ({...prev, ...reserved}));
                const timeout = window.setTimeout(
                    () => cancelReservation(uploadsToWarmup, "timeout"),
                    expires_seconds * 1000
                );
                timeoutsRef.current.push(timeout);
            } else {
                setFailedReservations(prev => [...prev, ...uploadsToWarmup]);
            }
            isRunningRef.current = false;
            setIsRunning(false);
        })
    }, [
        totalUploadEta,
        uploads,
        enabled,
        uploadDurations,
        isRunning
    ])

    const commitReservations = () => {
        timeoutsRef.current.forEach(t => clearTimeout(t));
        timeoutsRef.current = [];
    };

    const getReservedInstance = (uploadId: string) => reservedUploadIdsRef.current[uploadId];

    return {
        reservedInstances: reservedUploadIds,
        isReservationActive: isRunning,
        cancelReservation,
        commitReservations,
        getReservedInstance
    };
}
